/**
 * bubbleBlowPipeline — Touch blows soap bubbles that float up and pop.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface BubbleBlowPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  uPointer: WebGLUniformLocation | null;
  uPointerActive: WebGLUniformLocation | null;
  uPointerVel: WebGLUniformLocation | null;
  uClickPos: WebGLUniformLocation | null;
  uClickAge: WebGLUniformLocation | null;
  bands: Float32Array;
  smoothX: number;
  smoothY: number;
  prevX: number;
  prevY: number;
  clicks: { x: number; y: number; t: number }[];
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
uniform vec2 uPointer;
uniform float uPointerActive;
uniform vec2 uPointerVel;
uniform vec4 uClickPos;
uniform vec4 uClickAge;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return v;
}


void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = vec3(0.04, 0.06, 0.12);
  vec3 col = bg;

  vec2 pPos = (uPointer - 0.5 * uResolution) / uResolution.y;
  pPos.y = -pPos.y;
  vec2 toP = pPos - p;
  float pDist = length(toP);

  float clickE = 0.0;
  for (int i = 0; i < 2; i++) {
    vec2 cp = vec2(uClickPos[i*2], uClickPos[i*2+1]);
    float ca = uClickAge[i];
    if (ca >= 0.0 && ca < 4.0 && cp.x >= 0.0) {
      vec2 cPos = (cp - 0.5 * uResolution) / uResolution.y;
      cPos.y = -cPos.y;
      float cd = length(p - cPos);
      float radius = ca * 0.30;
      float band = exp(-pow((cd - radius) * 15.0, 2.0));
      float fade = 1.0 / (1.0 + ca * 1.5);
      clickE += band * fade;
    }
  }

  // AMBIENT: Bubbles constantly rising from the bottom of the screen.
  // Different sizes and speeds, with iridescent edges.
  vec3 bubbleTints[5];
  bubbleTints[0] = vec3(0.5, 0.7, 0.95);
  bubbleTints[1] = vec3(0.7, 0.85, 0.95);
  bubbleTints[2] = vec3(0.85, 0.7, 0.95);
  bubbleTints[3] = vec3(0.95, 0.85, 0.7);
  bubbleTints[4] = vec3(0.85, 0.95, 0.85);
  // 25 bubbles with different sizes, speeds, lateral drift.
  for (int bi = 0; bi < 25; bi++) {
    float fb = float(bi);
    float seed = hash21(vec2(fb, 1.0));
    float speed = 0.10 + seed * 0.15;
    float yStart = hash21(vec2(fb, 2.0));
    float size = 0.012 + 0.020 * hash21(vec2(fb, 3.0));
    float wobblePhase = hash21(vec2(fb, 4.0)) * 6.28;
    // Position: rises from below.
    float y = mod(yStart + uTime * speed + 0.7, 1.4) - 0.7;
    float x = (hash21(vec2(fb, 5.0)) * 1.6 - 0.8) + 0.05 * sin(uTime * 1.5 + wobblePhase);
    vec2 bpos = vec2(x, y);
    float bd = length(p - bpos);
    if (bd < size) {
      // Bubble edge.
      float edge = 1.0 - smoothstep(0.0, 0.004, abs(bd - size));
      col += vec3(0.7, 0.85, 1.0) * edge * 0.8;
      // Iridescent interior — color shifts with position.
      float irid = 0.5 + 0.5 * sin((bd * 30.0 + uTime * 2.0) * 5.0);
      vec3 tint = bubbleTints[int(fb * 7.0) % 5];
      col += tint * (1.0 - bd / size) * 0.3 * irid;
      // Highlight spot (top-left).
      vec2 hlOff = vec2(-0.4, 0.4) * size;
      float hlD = length(p - (bpos + hlOff));
      col += vec3(1.0, 1.0, 1.0) * exp(-hlD * 80.0) * 1.2;
      // Bottom shadow.
      vec2 shOff = vec2(0.4, -0.4) * size;
      float shD = length(p - (bpos + shOff));
      col -= vec3(0.05) * exp(-shD * 50.0);
    }
  }
  // Touch creates a bubble at the cursor.
  if (uPointerActive > 0.001) {
    float bubbleR = 0.05 + 0.02 * bass;
    float bd = length(p - pPos);
    if (bd < bubbleR) {
      float edge = 1.0 - smoothstep(0.0, 0.006, abs(bd - bubbleR));
      col += vec3(0.7, 0.85, 1.0) * edge * 0.7 * uPointerActive;
      vec2 hlOff = vec2(-0.4, 0.4) * bubbleR;
      float hlD = length(p - (pPos + hlOff));
      col += vec3(1.0, 1.0, 1.0) * exp(-hlD * 80.0) * uPointerActive * 1.2;
    }
  }
  // Click = a bubble that floats up and pops.
  for (int i = 0; i < 2; i++) {
    if (uClickPos[i*2] >= 0.0) {
      vec2 cp = vec2(uClickPos[i*2], uClickPos[i*2+1]);
      vec2 cPos = (cp - 0.5 * uResolution) / uResolution.y;
      cPos.y = -cPos.y;
      float age = uClickAge[i];
      vec2 bPos = cPos + vec2(0.05 * sin(age * 3.0 + float(i)), age * 0.25);
      float bR = 0.06 + 0.02 * bass;
      float bd = length(p - bPos);
      if (bd < bR) {
        float edge = 1.0 - smoothstep(0.0, 0.008, abs(bd - bR));
        col += vec3(0.7, 0.85, 1.0) * edge * 0.8;
        vec2 hlOff = vec2(-0.4, 0.4) * bR;
        float hlD = length(p - (bPos + hlOff));
        col += vec3(1.0, 1.0, 1.0) * exp(-hlD * 80.0) * 1.5;
      }
      // Pop flash near end of life.
      if (age > 3.0) {
        float fade = (age - 3.0) / 1.0;
        col += vec3(0.95, 0.95, 1.0) * exp(-length(p - bPos) * 15.0) * fade * 2.0;
      }
    }
  }
  col += vec3(0.85, 0.95, 1.0) * clickE * 0.6;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

export function computeBubbleBlowBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeBubbleBlowRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createBubbleBlowPipeline(gl: WebGL2RenderingContext): BubbleBlowPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    uPointer: gl.getUniformLocation(program, "uPointer"),
    uPointerActive: gl.getUniformLocation(program, "uPointerActive"),
    uPointerVel: gl.getUniformLocation(program, "uPointerVel"),
    uClickPos: gl.getUniformLocation(program, "uClickPos"),
    uClickAge: gl.getUniformLocation(program, "uClickAge"),
    bands: new Float32Array(9),
    smoothX: 0,
    smoothY: 0,
    prevX: 0,
    prevY: 0,
    clicks: [],
  };
}

export function bubbleBlowUpdatePointer(
  pipe: BubbleBlowPipeline,
  x: number,
  y: number,
  active: boolean,
  now: number,
): void {
  const dx = x - pipe.prevX;
  const dy = y - pipe.prevY;
  const vx = (pipe as any)._velX ?? 0;
  const vy = (pipe as any)._velY ?? 0;
  (pipe as any)._velX = vx + (dx - vx) * 0.30;
  (pipe as any)._velY = vy + (dy - vy) * 0.30;
  pipe.prevX = x;
  pipe.prevY = y;
  pipe.smoothX += (x - pipe.smoothX) * 0.20;
  pipe.smoothY += (y - pipe.smoothY) * 0.20;
  const target = active ? 1 : 0;
  (pipe as any)._active = ((pipe as any)._active ?? 0) + (target - ((pipe as any)._active ?? 0)) * 0.15;
}

export function bubbleBlowPushClick(
  pipe: BubbleBlowPipeline,
  x: number, y: number, now: number,
): void {
  pipe.clicks.push({ x, y, t: now });
  if (pipe.clicks.length > 2) pipe.clicks.shift();
}

export function drawBubbleBlow(
  gl: WebGL2RenderingContext,
  pipe: BubbleBlowPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const cp = [0, 0, 0, 0];
  const ca = [0, 0, 0, 0];
  const c0 = pipe.clicks[0];
  const c1 = pipe.clicks[1];
  if (c0) {
    const age = t - c0.t;
    if (age < 4.0) { cp[0] = c0.x; cp[1] = c0.y; ca[0] = age; }
    else { cp[0] = -1; ca[0] = 4.0; }
  } else { cp[0] = -1; ca[0] = 4.0; }
  if (c1) {
    const age = t - c1.t;
    if (age < 4.0) { cp[2] = c1.x; cp[3] = c1.y; ca[1] = age; }
    else { cp[2] = -1; ca[1] = 4.0; }
  } else { cp[2] = -1; ca[1] = 4.0; }
  if ((pipe as any)._active === undefined) (pipe as any)._active = 0;
  gl.uniform2f(pipe.uResolution!, width, height);
  gl.uniform1f(pipe.uTime!, t);
  gl.uniform3f(pipe.uBass!, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(pipe.uMid!, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(pipe.uHigh!, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.uniform2f(pipe.uPointer!, pipe.smoothX, pipe.smoothY);
  gl.uniform1f(pipe.uPointerActive!, (pipe as any)._active);
  gl.uniform2f(pipe.uPointerVel!, (pipe as any)._velX || 0, (pipe as any)._velY || 0);
  gl.uniform4f(pipe.uClickPos!, cp[0], cp[1], cp[2], cp[3]);
  gl.uniform4f(pipe.uClickAge!, ca[0], ca[1], ca[2], ca[3]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
